import permissions from "./permissions";
import StyleguideNode from "../model/styleguideNode";
import StyleguideTree from "../model/styleguideTree";
import Barrel from "../model/Barrel";
import BarrelUser from "../model/BarrelUser";
import BasicRecord from "./BasicRecord";

function getSelectableParentStyleguides({
    isOrganizationStyleguide, currentStyleguides, currentUser, id, parentId, users
}: {
    isOrganizationStyleguide: boolean;
    currentStyleguides: Barrel[];
    currentUser: { role: string; };
    id: string;
    parentId?: string | null;
    users: BarrelUser[];
}): Barrel[] {
    return currentStyleguides.filter(styleguide => {
        let canSelectParent;
        if (isOrganizationStyleguide) {
            canSelectParent = permissions.canUpdateStyleguide(currentUser);
        } else {
            const styleguideOwner = styleguide.users.find(user => user.role === "owner");
            const currentStyleguideOwner = users.find(user => user.role === "owner");
            canSelectParent = styleguideOwner && currentStyleguideOwner &&
                styleguideOwner.user._id === currentStyleguideOwner.user._id;
        }

        return canSelectParent &&
            styleguide._id !== id &&
            styleguide._id !== parentId &&
            styleguide.status === "active" &&
            !(styleguide.ancestors || []).includes(id);
    });
}

function generateTreesAndNodes(styleguidesData: Barrel[]): {
    trees: StyleguideTree[];
    nodes: StyleguideNode[];
} {
    // Build a dictionary for nodes
    const nodes = styleguidesData.map((styleguide, index) => new StyleguideNode(styleguide, index));
    const nodeDict = nodes.reduce((result, node) => {
        result[node.id] = node;
        return result;
    }, {} as BasicRecord<StyleguideNode>);

    // Add children recursively
    styleguidesData.forEach(styleguide => {
        if (styleguide.parent) {
            const node = nodeDict[styleguide._id];
            const parentNode = nodeDict[styleguide.parent];

            if (parentNode) {
                parentNode.addChild(node);
            } else {
                // If not invited to parent styleguide, make it a tree
                delete nodeDict[styleguide._id].parentId;
            }
        }
    });

    // Generate tree models from root level nodes
    const trees = Object.keys(nodeDict).filter(id => nodeDict[id].isRoot()).map(id => new StyleguideTree(nodeDict[id]));

    return {
        trees,
        nodes
    };
}

/**
 * Returns ids of the styleguide and its children
 * @param styleguides keyed with stid
 * @param stid target styleguide's id
 * @returns array of styleguide ids
 */
function getStyleguideAndItsDescendants(styleguides: BasicRecord<Barrel>, stid: string): string[] {
    return Object.values(styleguides)
        .filter(({ _id, ancestors }) => _id === stid || ancestors!.includes(stid))
        .map(({ _id }) => _id);
}

export {
    generateTreesAndNodes,
    getSelectableParentStyleguides,
    getStyleguideAndItsDescendants
};
